import { useState } from "react";
import { X, Heart } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";

/**
 * Generic anonymous-note modal.
 * Props:
 *   subjectLabel — display name of the thing being reacted to (e.g. work.author_pseudonym or character.name)
 *   submitPath — API endpoint (e.g. `/works/{id}/reactions` or `/characters/{id}/reactions`)
 *   copy — { title, subtitle, placeholder } (all optional; sensible defaults for manuscript context)
 *   onClose, onCreated — usual modal handlers
 */
export default function ReactionModal({ work, subjectLabel, submitPath, copy = {}, onClose, onCreated }) {
  // Backwards-compat: if a `work` was passed, derive submitPath + subjectLabel from it.
  const path = submitPath || (work && `/works/${work.id}/reactions`);
  const label = subjectLabel || (work && work.author_pseudonym) || "the author";
  const {
    title = `Leave a note for ${label}`,
    subtitle = "The line the manuscript left with you. Only the author will read it.",
    placeholder = "The one line I'll carry with me…",
  } = copy;

  const [line, setLine] = useState("");
  const [saving, setSaving] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (line.trim().length < 3) { toast.error("Say at least a few words"); return; }
    setSaving(true);
    try {
      await api.post(path, { line: line.trim() });
      toast.success("Sent anonymously.");
      onCreated?.();
      onClose();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[60] flex items-end sm:items-center justify-center bg-black/70 backdrop-blur-sm p-4" onClick={onClose} data-testid="reaction-modal">
      <div className="relative w-full max-w-md bg-[#161412] border border-[rgba(217,180,114,0.25)] rounded-sm p-6 sm:p-8" onClick={(e) => e.stopPropagation()}>
        <button onClick={onClose} className="absolute top-4 right-4 text-[#6E675E] hover:text-[#F7F3EC]" data-testid="reaction-close">
          <X className="w-5 h-5" />
        </button>
        <div className="flex items-center gap-2 mb-2">
          <Heart className="w-4 h-4 text-[#D9B472]" />
          <div className="font-mono-brass text-xs uppercase tracking-widest text-[#D9B472]">One line, sent anonymously</div>
        </div>
        <h3 className="font-serif-display text-2xl text-[#F7F3EC] mb-2">{title}</h3>
        <p className="text-sm text-[#A8A095] mb-5 italic font-serif-body">{subtitle}</p>
        <form onSubmit={submit} className="space-y-4">
          <textarea
            value={line}
            onChange={(e) => setLine(e.target.value)}
            rows={4}
            maxLength={280}
            placeholder={placeholder}
            className="w-full bg-[#0D0C0A] border border-[rgba(217,180,114,0.15)] focus:border-[#D9B472] outline-none rounded-sm px-4 py-3 text-[#F7F3EC] font-serif-body text-base italic resize-none"
            data-testid="reaction-textarea"
          />
          <div className="flex items-center justify-between text-[10px] font-mono-brass text-[#6E675E] uppercase tracking-widest">
            <span>Anonymous · one-way</span>
            <span data-testid="reaction-charcount">{line.length}/280</span>
          </div>
          <button type="submit" disabled={saving} className="btn-buy w-full rounded-sm py-3.5 font-mono-brass text-xs uppercase tracking-widest font-semibold" data-testid="reaction-submit">
            {saving ? "Sending…" : "Send anonymously"}
          </button>
        </form>
      </div>
    </div>
  );
}
